import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Archive, ArchiveRestore, GraduationCap, Users, Search, Mail, Phone, CheckCircle2, XCircle, Clock } from "lucide-react";

const STATUS_STYLES = {
    present: { icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800" },
    absent: { icon: XCircle, cls: "bg-rose-100 text-rose-800" },
    late: { icon: Clock, cls: "bg-amber-100 text-amber-800" },
};

export default function AdminTeachersDirectory() {
    const [rows, setRows] = React.useState([]);
    const [q, setQ] = React.useState("");
    const [showArchived, setShowArchived] = React.useState(false);

    const load = React.useCallback(() => {
        api.get("/admin/teachers-directory", { params: { include_archived: showArchived } })
            .then(r => setRows(r.data));
    }, [showArchived]);
    React.useEffect(() => { load(); }, [load]);

    const filtered = rows.filter(t =>
        !q || `${t.full_name} ${t.email} ${t.phone || ""}`.toLowerCase().includes(q.toLowerCase())
    );

    async function archive(id, name) {
        if (!window.confirm(`Archive ${name}? They'll be removed from active classes and hidden from lists.`)) return;
        await api.delete(`/admin/teachers/${id}`);
        toast.success(`${name} archived`);
        load();
    }
    async function restore(id, name) {
        await api.post(`/admin/teachers/${id}/restore`);
        toast.success(`${name} restored`);
        load();
    }
    async function changeType(id, staff_type) {
        await api.patch(`/admin/teachers/${id}/staff-type`, { staff_type });
        toast.success("Staff type updated");
        load();
    }

    return (
        <div>
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="overline text-brand-stone">Admin</div>
                    <h1 className="font-serif text-3xl">Teachers directory</h1>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant={showArchived ? "default" : "outline"}
                        size="sm"
                        onClick={() => setShowArchived(v => !v)}
                        data-testid="toggle-archived-teachers-btn"
                        className={showArchived ? "bg-primary hover:bg-primary/90" : ""}
                    >
                        {showArchived ? "Hide archived" : "Show archived"}
                    </Button>
                </div>
            </div>

            <div className="relative mb-4 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-stone" />
                <Input
                    placeholder="Search teacher by name, email, phone…"
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    data-testid="teachers-directory-search"
                    className="pl-9"
                />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                {filtered.map(t => (
                    <Card key={t.id} data-testid={`teacher-card-${t.id}`}>
                        <CardContent className="p-5">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className="font-dash text-lg font-semibold">{t.full_name}</div>
                                        {t.archived && <Badge className="bg-rose-100 text-rose-800 border-0">Archived</Badge>}
                                    </div>
                                    <div className="text-xs text-brand-stone mt-1 flex items-center gap-3 flex-wrap">
                                        <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{t.email}</span>
                                        {t.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{t.phone}</span>}
                                    </div>
                                </div>
                                <div className="flex gap-1 shrink-0">
                                    {t.archived ? (
                                        <Button size="sm" variant="outline" onClick={() => restore(t.id, t.full_name)} data-testid={`restore-teacher-${t.id}`} className="gap-1.5 h-8">
                                            <ArchiveRestore className="w-3.5 h-3.5" /> Restore
                                        </Button>
                                    ) : (
                                        <Button size="sm" variant="outline" onClick={() => archive(t.id, t.full_name)} data-testid={`archive-teacher-${t.id}`} className="gap-1.5 h-8 text-brand-stone hover:text-destructive">
                                            <Archive className="w-3.5 h-3.5" /> Archive
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 flex items-center gap-2 text-xs">
                                <span className="text-brand-stone">Staff type</span>
                                <Select value={t.staff_type || "teacher"} onValueChange={v => changeType(t.id, v)}>
                                    <SelectTrigger className="h-8 w-48 text-xs" data-testid={`staff-type-${t.id}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="teacher" data-testid={`staff-type-teacher-${t.id}`}>Teacher ($50/day)</SelectItem>
                                        <SelectItem value="teacher_volunteer" data-testid={`staff-type-volunteer-${t.id}`}>Teacher/Volunteer ($25/section)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="mt-4 grid grid-cols-2 gap-3">
                                <Stat icon={GraduationCap} label="Classes" value={t.classes.length} />
                                <Stat icon={Users} label="Students" value={t.student_count} />
                            </div>

                            {t.classes.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    {t.classes.map(c => (
                                        <Badge key={c.id} className="bg-primary/10 text-primary border-0">
                                            {c.name} · {c.student_count}
                                        </Badge>
                                    ))}
                                </div>
                            )}

                            {t.recent_attendance?.length > 0 && (
                                <div className="mt-4 pt-3 border-t border-border/60">
                                    <div className="overline text-brand-stone mb-2">Recent attendance</div>
                                    <div className="flex gap-1.5 flex-wrap">
                                        {t.recent_attendance.map(a => {
                                            const S = STATUS_STYLES[a.status] || {};
                                            const Icon = S.icon;
                                            return (
                                                <Badge key={a.date + a.status} className={`${S.cls || "bg-secondary text-brand-ink"} border-0 gap-1`}>
                                                    {Icon && <Icon className="w-3 h-3" />}
                                                    {a.date}
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))}
                {filtered.length === 0 && (
                    <div className="md:col-span-2 text-center py-16 text-brand-stone">No teachers.</div>
                )}
            </div>
        </div>
    );
}

function Stat({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-2 border border-border rounded-lg px-3 py-2">
            <Icon className="w-4 h-4 text-primary" />
            <div className="text-sm">
                <div className="font-semibold">{value}</div>
                <div className="text-[11px] text-brand-stone">{label}</div>
            </div>
        </div>
    );
}
